import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { X, FileSpreadsheet } from 'lucide-react';
import * as XLSX from 'xlsx';
import { getProductBrand } from '../utils/positionBrand';

interface Product {
  id_catalogo: number;
  title: string;
  imagen: string;
  sale_price: string;
  original_price: string;
  discounted_price: string;
  marca?: string;
  Marca?: string;
  tag_MARCA?: string;
  sitio: string;
  page?: number;
  orderInPage?: number;
  apariciones?: string | any[] | any;
  url: string;
  Cat?: string;
  categoria?: string;
}

interface CategoryData {
  name: string;
  products: Product[];
  pageRange: { min: number; max: number };
}

interface PositionBrandReportModalProps {
  isOpen: boolean;
  onClose: () => void;
  category: CategoryData;
  selectedBrands: string[];
  selectedSite?: string;
  groupBy?: 'site' | 'category';
}

interface BrandStats {
  brand: string;
  presence: number;
  bestPosition: number;
  avgPrice: number;
  minPrice: number;
  maxPrice: number;
  count: number;
  totalPositions: number;
}

const PositionBrandReportModal: React.FC<PositionBrandReportModalProps> = ({
  isOpen,
  onClose,
  category,
  selectedBrands,
  selectedSite,
  groupBy
}) => {
  const { t } = useTranslation();

  // Resolución de marca POR PRODUCTO (coalesce tag_MARCA → Marca → marca).
  // Antes se usaba `brandColumnName` global: si el dataset se resolvía por
  // tag_MARCA pero un producto tenía tag_MARCA null (p.ej. sitios sin tags),
  // devolvía '' y se descartaba aunque tuviera Marca nativa → "No se
  // encontraron marcas" (bug PT-#39).
  const getBrand = (product: Product): string => getProductBrand(product);

  // Helper function to parse price
  const parsePrice = (priceStr: string): number => {
    if (!priceStr || priceStr === '' || priceStr === 'null') return 0;
    
    // Simple parse - prices come as numeric strings from database
    const numValue = parseFloat(priceStr);
    if (!isNaN(numValue)) {
      return numValue;
    }
    
    return 0;
  };

  // Format price for display
  const formatPrice = (price: number): string => {
    return '$' + price.toLocaleString('es-AR', { 
      minimumFractionDigits: 0,
      maximumFractionDigits: 0 
    });
  };

  // Calculate statistics by page
  const reportData = useMemo(() => {
    const pageReports: { [page: number]: BrandStats[] } = {};
    
    // Group products by page
    const productsByPage: { [page: number]: Product[] } = {};
    category.products.forEach(product => {
      const page = product.page ?? 0;
      if (!productsByPage[page]) {
        productsByPage[page] = [];
      }
      productsByPage[page].push(product);
    });
    
    // Calculate stats for each page
    Object.entries(productsByPage).forEach(([pageStr, products]) => {
      const page = parseInt(pageStr);
      const brandStats: { [brand: string]: BrandStats } = {};
      
      products.forEach(product => {
        const brand = getBrand(product);
        if (!brand) return;
        
        // If brands are selected, only include selected brands
        if (selectedBrands.length > 0 && !selectedBrands.includes(brand)) {
          return;
        }
        
        const price = parsePrice(product.sale_price);
        
        if (!brandStats[brand]) {
          brandStats[brand] = {
            brand,
            presence: 0,
            bestPosition: product.orderInPage ?? 999,
            avgPrice: 0,
            minPrice: price,
            maxPrice: price,
            count: 0,
            totalPositions: 0
          };
        }
        
        brandStats[brand].count++;
        brandStats[brand].avgPrice += price;
        brandStats[brand].bestPosition = Math.min(brandStats[brand].bestPosition, product.orderInPage ?? 999);
        brandStats[brand].minPrice = Math.min(brandStats[brand].minPrice, price);
        brandStats[brand].maxPrice = Math.max(brandStats[brand].maxPrice, price);
      });
      
      // Calculate final stats - use actual number of products on the page
      const totalPositions = products.length;
      const brandStatsArray = Object.values(brandStats).map(stats => ({
        ...stats,
        presence: (stats.count / totalPositions) * 100,
        avgPrice: stats.avgPrice / stats.count,
        totalPositions: totalPositions
      }));
      
      // Sort by presence % (descending - highest first)
      brandStatsArray.sort((a, b) => b.presence - a.presence);
      
      pageReports[page] = brandStatsArray;
    });
    
    return pageReports;
  }, [category, selectedBrands]);

  // Export to Excel function
  const exportToExcel = () => {
    // Create workbook
    const wb = XLSX.utils.book_new();
    
    // Create data for all pages combined
    const allData: any[] = [];
    
    Object.entries(reportData)
      .sort(([a], [b]) => parseInt(a) - parseInt(b))
      .forEach(([page, brands]) => {
        // Add page header
        allData.push({
          [t('position.brand')]: `${t('position.page_report', { page })}`,
          [t('position.presence')]: '',
          [t('position.quantity')]: '',
          [t('position.best_position')]: '',
          [t('position.avg_price')]: '',
          [t('position.min_price')]: '',
          [t('position.max_price')]: ''
        });
        
        // Add brand data
        brands.forEach(brand => {
          allData.push({
            [t('position.brand')]: brand.brand,
            [t('position.presence')]: `${brand.presence.toFixed(1)}%`,
            [t('position.quantity')]: `${brand.count}/${brand.totalPositions}`,
            [t('position.best_position')]: brand.bestPosition,
            [t('position.avg_price')]: brand.avgPrice,
            [t('position.min_price')]: brand.minPrice,
            [t('position.max_price')]: brand.maxPrice
          });
        });
        
        // Add empty row between pages
        allData.push({
          [t('position.brand')]: '',
          [t('position.presence')]: '',
          [t('position.quantity')]: '',
          [t('position.best_position')]: '',
          [t('position.avg_price')]: '',
          [t('position.min_price')]: '',
          [t('position.max_price')]: ''
        });
      });
    
    // Create worksheet
    const ws = XLSX.utils.json_to_sheet(allData);
    
    // Set column widths
    ws['!cols'] = [
      { wch: 25 }, // Brand
      { wch: 12 }, // Presence %
      { wch: 10 }, // Quantity
      { wch: 15 }, // Best Position
      { wch: 15 }, // Avg Price
      { wch: 15 }, // Min Price
      { wch: 15 }  // Max Price
    ];
    
    // Apply currency format to price columns
    const range = XLSX.utils.decode_range(ws['!ref'] || 'A1');
    const priceColumns = [
      t('position.avg_price'),
      t('position.min_price'),
      t('position.max_price')
    ];
    
    // Get column indices for price columns
    const headers = Object.keys(allData[0] || {});
    const priceColumnIndices = headers
      .map((header, index) => priceColumns.includes(header) ? index : -1)
      .filter(index => index !== -1);
    
    // Apply number format to price cells
    for (let row = 1; row <= range.e.r; row++) {
      for (const colIndex of priceColumnIndices) {
        const cellAddress = XLSX.utils.encode_cell({ r: row, c: colIndex });
        if (ws[cellAddress] && typeof ws[cellAddress].v === 'number') {
          ws[cellAddress].z = '"$"#,##0';
        }
      }
    }
    
    // Add worksheet to workbook
    XLSX.utils.book_append_sheet(wb, ws, t('position.report'));
    
    // Generate filename
    const date = new Date();
    const dateStr = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
    const filename = `reporte_marcas_${category.name.replace(/\s+/g, '_')}_${dateStr}.xlsx`;
    
    // Write file
    XLSX.writeFile(wb, filename);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b">
          <h2 className="text-lg font-semibold">
            {groupBy === 'site' && selectedSite ? `${selectedSite} - ${category.name}` : category.name}
          </h2>
          <div className="flex items-center gap-2">
            <button
              onClick={exportToExcel}
              className="px-3 py-1.5 bg-white text-green-600 border border-green-600 rounded-lg hover:bg-green-50 transition-colors flex items-center gap-2 text-sm"
            >
              <FileSpreadsheet className="h-4 w-4" />
              {t('position.export_excel')}
            </button>
            <button
              onClick={onClose}
              className="p-1 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <X className="h-5 w-5 text-gray-500" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-4 overflow-y-auto max-h-[calc(90vh-80px)]">
          {selectedBrands.length > 0 && (
            <div className="mb-4">
              <p className="text-sm text-gray-500">
                {t('position.selected_brands')}: {selectedBrands.join(', ')}
              </p>
            </div>
          )}

          {/* Page reports */}
          <div className="space-y-6">
            {Object.entries(reportData)
              .sort(([a], [b]) => parseInt(a) - parseInt(b))
              .map(([page, brands]) => (
                <div key={page} className="border rounded-lg p-4">
                  <h4 className="text-sm font-semibold text-gray-700 mb-3">
                    {t('position.page_report', { page })}
                  </h4>
                  
                  {brands.length === 0 ? (
                    <p className="text-sm text-gray-500">{t('position.no_brands_found')}</p>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b">
                            <th className="text-left py-2 px-3">{t('position.brand')}</th>
                            <th className="text-center py-2 px-3">{t('position.presence')}</th>
                            <th className="text-center py-2 px-3">{t('position.quantity')}</th>
                            <th className="text-center py-2 px-3">{t('position.best_position')}</th>
                            <th className="text-right py-2 px-3">{t('position.avg_price')}</th>
                            <th className="text-right py-2 px-3">{t('position.min_price')}</th>
                            <th className="text-right py-2 px-3">{t('position.max_price')}</th>
                          </tr>
                        </thead>
                        <tbody>
                          {brands.map((brandStats, index) => (
                            <tr key={brandStats.brand} className={index % 2 === 0 ? 'bg-gray-50' : ''}>
                              <td className="py-2 px-3 font-medium">{brandStats.brand}</td>
                              <td className="text-center py-2 px-3">{brandStats.presence.toFixed(1)}%</td>
                              <td className="text-center py-2 px-3">{brandStats.count}/{brandStats.totalPositions}</td>
                              <td className="text-center py-2 px-3">{brandStats.bestPosition}</td>
                              <td className="text-right py-2 px-3">{formatPrice(brandStats.avgPrice)}</td>
                              <td className="text-right py-2 px-3">{formatPrice(brandStats.minPrice)}</td>
                              <td className="text-right py-2 px-3">{formatPrice(brandStats.maxPrice)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PositionBrandReportModal;